/**
 * Human-readable labels for Tendril capability names in the streaming status
 * checklist (tendril-streaming-responses, post-UAT polish).
 *
 * WHY THIS EXISTS
 * The backend emits status frames as `Running ${capability.name}…`
 * (backend/src/lib/tendril/agent-loop.ts), so the raw snake_case identifier
 * reaches the UI verbatim — the first live UAT showed users
 * "Running list_workshops…" and "get_workshop_details". The UI/UX design called
 * for human-readable progress copy instead.
 *
 * Labelling happens CLIENT-SIDE rather than in the frame so this needs no
 * protocol change and no backend redeploy. The cleaner long-term fix is for the
 * `status` frame to carry a `capability` field (and optionally a server-authored
 * label sourced from the capability manifest's own display metadata) — worth
 * doing when the protocol is next revised, at which point this module becomes
 * the fallback path rather than the primary one.
 *
 * DESIGN: a generic humanizer with a small override map, NOT an exhaustive
 * lookup table. There are dozens of capabilities across three modules and more
 * arrive with every feature; an exhaustive map would silently rot and start
 * showing raw names again. The generic path always produces something
 * presentable, and overrides exist only where the generic result reads badly.
 */

/**
 * Present-participle overrides for capabilities whose generic humanization
 * reads awkwardly. Keep this SMALL — only add an entry when the generic output
 * is genuinely poor, not merely imperfect.
 */
const LABEL_OVERRIDES = {
  list_workshops: 'Looking up workshops',
  get_workshop_details: 'Reading workshop details',
  find_sessions: 'Finding workshop sessions',
  get_session_roster: 'Pulling the session roster',
  get_my_enrollments: 'Checking your enrollments',
  get_my_session_access: 'Checking your meeting link and invite',
  resend_my_invite: 'Re-sending your calendar invite',
  get_team_invite_status: 'Checking your team’s invite status',
  enroll_in_session: 'Enrolling you in the session',
  enroll_team_member_in_session: 'Enrolling your team member',
  cancel_enrollment: 'Cancelling the enrollment',
  list_opcos: 'Looking up OpCos',
  list_my_initiatives: 'Looking up your initiatives',
  list_opco_initiatives: 'Looking up the OpCo’s initiatives',
  analyze_portfolio_initiatives: 'Analyzing portfolio initiatives',
  update_initiative: 'Updating the initiative',
  submit_feedback: 'Filing your feedback',
};

/**
 * Verb prefixes we can turn into a present participle generically, longest
 * first so `get_my_` is considered before `get_`.
 */
const VERB_PREFIXES = [
  ['list_', 'Looking up '],
  ['get_my_', 'Checking your '],
  ['get_', 'Reading '],
  ['search_', 'Searching '],
  ['analyze_', 'Analyzing '],
  ['create_', 'Creating '],
  ['update_', 'Updating '],
  ['delete_', 'Deleting '],
  ['cancel_', 'Cancelling '],
  ['submit_', 'Submitting '],
  ['view_', 'Opening '],
];

/**
 * Turn a raw capability identifier into human-readable progress copy.
 *
 * @param {string} name — e.g. "get_workshop_details"
 * @returns {string} e.g. "Reading workshop details"
 */
export function capabilityLabel(name) {
  if (!name || typeof name !== 'string') return 'Working';

  const key = name.trim();
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];

  for (const [prefix, phrase] of VERB_PREFIXES) {
    if (key.startsWith(prefix)) {
      const rest = key.slice(prefix.length).replace(/_/g, ' ').trim();
      if (rest) return `${phrase}${rest}`;
    }
  }

  // Unknown verb shape: at least strip the snake_case and sentence-case it, so
  // a brand-new capability reads as "Some new thing" rather than
  // "some_new_thing".
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Recover a human label from a server-authored status frame.
 *
 * The backend's format is `Running <capability>…`. When it matches we humanize
 * the captured identifier; otherwise we pass the server's text through
 * unchanged, so a future server-side label (or any other status message) is
 * displayed as-authored rather than mangled.
 *
 * @param {string} text — the status frame's `text`
 * @returns {{ label: string, capability: string | null }}
 */
export function labelFromStatusText(text) {
  if (!text || typeof text !== 'string') return { label: 'Working', capability: null };

  const match = text.match(/^Running\s+([A-Za-z0-9_]+)\s*…?$/);
  if (match) {
    return { label: capabilityLabel(match[1]), capability: match[1] };
  }

  return { label: text, capability: null };
}
